import * as THREE from "three";
import { Loader } from "../../assets/loader";
import { Utils } from "../../../utils/utils";
import AppData from "../../../app.data";
import { ArtworkWrapper } from "@/types/artworksData";
import { getArtworkMediaUrl } from "../../../utils/media-cache-policy.utils";
import { inferSlotTarget } from "../../../utils/runtime-registry.utils";

const appData = new AppData();

const getArtworkMapIntensity = (): number => {
    const moods = appData.spaceData?.data?.moods || [];
    const requestedMoodId = Number.isInteger(appData.galleryData?.data?.mood) ? appData.galleryData.data.mood : 0;
    const mood = moods[requestedMoodId] || moods[0];
    const mapIntensity = Number(mood?.renderSettings?.artworkMapIntensity);
    return Number.isFinite(mapIntensity) ? mapIntensity : 1.0;
}

const isHighTextureQuality = (): boolean => {
    const renderQuality = appData.renderService?.renderQuality;
    return renderQuality === "MQ" || renderQuality === "HQ";
}

type ArtworkMediaRoute =
    | { ok: true; route: 'image' | 'video' }
    | {
        ok: false;
        reason: 'media_type_missing' | 'media_type_unsupported' | 'mime_type_unsupported' | 'declared_mime_mismatch';
    };

const routeFromMimeType = (value?: string | null): 'image' | 'video' | null => {
    const mimeType = String(value || '').split(';')[0].trim().toLowerCase();
    if (!mimeType) return null;
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    return null;
};

const routeFromCanonicalMediaUrl = (value?: string | null): 'image' | 'video' | null => {
    try {
        const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
        const path = new URL(String(value || ''), baseUrl).pathname.toLowerCase();
        if (/\/artworks\/(?:images|original-images)\//.test(path)) return 'image';
        if (/\/artworks\/videos\//.test(path)) return 'video';
        return null;
    } catch {
        return null;
    }
};

const SKETCHFAB_IMAGE_HOSTS = new Set([
    'media.sketchfab.com',
    'static.sketchfab.com',
]);

const routeFromProviderPreviewUrl = (value?: string | null): 'image' | null => {
    try {
        const url = new URL(String(value || ''));
        if (url.protocol !== 'https:' || url.username || url.password) return null;

        const hostname = url.hostname.toLowerCase();
        const pathname = url.pathname.toLowerCase();
        if (
            hostname === 'api.europeana.eu' &&
            pathname === '/thumbnail/v2/url.json' &&
            url.searchParams.get('type')?.toLowerCase() === 'image'
        ) {
            return 'image';
        }
        if (
            SKETCHFAB_IMAGE_HOSTS.has(hostname) &&
            /\.(?:avif|gif|jpe?g|png|webp)$/.test(pathname)
        ) {
            return 'image';
        }
        return null;
    } catch {
        return null;
    }
};

export const resolveArtworkMediaRoute = (
    artworkWrapper: ArtworkWrapper,
    selectedMediaUrl?: string
): ArtworkMediaRoute => {
    const activeArtwork = artworkWrapper?.artworks?.[artworkWrapper.activeArtwork];
    const declaredType = activeArtwork?.type;
    const rawMimeType = activeArtwork?.artworkData?.mimeType;
    const mimeRoute = routeFromMimeType(rawMimeType);

    if (!declaredType) {
        return { ok: false, reason: 'media_type_missing' };
    }
    if (declaredType === 'diorama') {
        const previewUrl = selectedMediaUrl || activeArtwork?.artworkData?.dataUrl;
        const previewRoute = routeFromCanonicalMediaUrl(previewUrl) || routeFromProviderPreviewUrl(previewUrl);
        if (!previewRoute) {
            return { ok: false, reason: 'media_type_unsupported' };
        }
        if (rawMimeType && !mimeRoute) {
            return { ok: false, reason: 'mime_type_unsupported' };
        }
        if (mimeRoute && mimeRoute !== previewRoute) {
            return { ok: false, reason: 'declared_mime_mismatch' };
        }
        return { ok: true, route: previewRoute };
    }
    if (declaredType !== 'image' && declaredType !== 'video') {
        return { ok: false, reason: 'media_type_unsupported' };
    }

    if (rawMimeType && !mimeRoute) {
        return { ok: false, reason: 'mime_type_unsupported' };
    }
    if (mimeRoute && mimeRoute !== declaredType) {
        return { ok: false, reason: 'declared_mime_mismatch' };
    }
    return { ok: true, route: declaredType };
};

export class ArtworkMedia {
    /**
     * Loads artwork graphics based on file type
     * Supports both video and image formats
     * @param loader Asset loader instance
     * @param utils Utils instance
     * @param artworkModel Model to apply graphics to
     * @param artowrkURL URL of the artwork resource
     * @param artworkWrapper Artwork configuration
     * @returns Promise resolving when graphics are loaded
     */
    static async loadNFTGraphics(loader: Loader, _utils: Utils, artworkModel: any, artowrkURL: string, artworkWrapper: ArtworkWrapper): Promise<{ outputWidth: number, outputHeight: number }> {
        try {
            const route = resolveArtworkMediaRoute(artworkWrapper, artowrkURL);
            if ('reason' in route) {
                throw new Error(`Artwork media routing rejected: ${route.reason}`);
            }
            if (route.route === 'video') {
                const result = await this.loadNFTVideo(artworkModel, artowrkURL, artworkWrapper);
                return result;
            } else {
                const result = await this.loadNFTImage(loader, artworkModel, artowrkURL, artworkWrapper);
                return result;
            }
        } catch (error) {
            console.error("ArtworkMedia: load failed");
            throw error instanceof Error
                ? error
                : new Error('Artwork media load failed');
        }
    }

    /**
     * Loads and sets up video textures for artwork
     * Handles video element creation and material configuration
     * @param artworkModel Model to apply video to
     * @param uri Video URL
     * @param artworkWrapper Optional artwork configuration
     * @returns Promise resolving when video is loaded
     */
    static async loadNFTVideo(artworkModel: any, uri: string, artworkWrapper?: ArtworkWrapper): Promise<{ outputWidth: number, outputHeight: number }> {
        try {
            return await new Promise((resolve, reject) => {
                const video = document.createElement("video")
                video.setAttribute('crossorigin', 'anonymous');
                video.setAttribute("allow", "autoplay")
                video.src = getArtworkMediaUrl(uri)
                video.muted = true;

                video.onloadeddata = function () {
                    try {
                        const width = video.videoWidth
                        const height = video.videoHeight
                        const imageRatio = 2 / Math.max(width, height);
                        const videoTexture = new THREE.VideoTexture(video);
                        videoTexture.needsUpdate = true;
                        videoTexture.wrapS = videoTexture.wrapT = THREE.RepeatWrapping;
                        videoTexture.repeat.set(1, 1);
                        videoTexture.colorSpace = THREE.SRGBColorSpace;
                        let outputWidth = isHighTextureQuality() ? 1024 : 512
                        let outputHeight = isHighTextureQuality() ? 1024 : 512
                        if (artworkWrapper) {
                            const activeArtwork = artworkWrapper.artworks[artworkWrapper.activeArtwork]
                            const resultingWidth = (activeArtwork.artworkData.height * width) / height;
                            artworkModel.scale.x = (resultingWidth * 0.01);
                            outputWidth = resultingWidth * 0.01
                            artworkModel.scale.y = (activeArtwork.artworkData.height * 0.01);
                            outputHeight = activeArtwork.artworkData.height * 0.01
                            let material: any = null
                            if (artworkWrapper?.artworks[artworkWrapper?.activeArtwork]?.artworkData?.affectedByLights) {
                                material = new THREE.MeshPhysicalMaterial({ color: 0xffffff, map: videoTexture, transparent: true, roughness: 1, metalness: 0, side: THREE.DoubleSide })
                                material.userData.mapIntensity = { value: getArtworkMapIntensity() };
                                material.onBeforeCompile = (shader: any) => {
                                    shader.uniforms.mapIntensity = material.userData.mapIntensity;
                                    shader.fragmentShader = shader.fragmentShader.replace(
                                        '#include <map_fragment>',
                                        `
                        #ifdef USE_MAP
                          vec4 sampledDiffuseColor = texture2D( map, vMapUv );
                          sampledDiffuseColor.rgb *= mapIntensity;
                          diffuseColor *= sampledDiffuseColor;
                        #endif
                        `
                                    );
                                    shader.fragmentShader = `uniform float mapIntensity;
                      
                      ` + shader.fragmentShader;
                                };
                            } else {
                                material = new THREE.MeshPhysicalMaterial({ map: videoTexture, roughness: 1, emissiveMap: videoTexture, emissive: new THREE.Color(0xffffff), emissiveIntensity: 2 })
                            }
                            artworkModel.material = material
                            artworkModel.material.needsUpdate = true;
                        } else {
                            artworkModel.scale.x = imageRatio * width;
                            outputWidth = imageRatio * width
                            artworkModel.scale.y = imageRatio * height;
                            outputHeight = imageRatio * height
                            artworkModel.material = new THREE.MeshPhysicalMaterial({ color: 0xffffff, map: videoTexture, transparent: true, side: THREE.DoubleSide, emissiveMap: videoTexture, emissiveIntensity: 1 })
                            artworkModel.material.needsUpdate = true;
                        }
                        const videoData = {
                            video: video,
                            artworkModel: artworkModel,
                            slotTarget: artworkWrapper?.slotTarget ?? inferSlotTarget(artworkModel)
                        }
                        artworkModel.userData.video = video
                        appData.videoList.push(videoData)
                        resolve({ outputWidth: outputWidth, outputHeight: outputHeight })
                    } catch (_error) {
                        reject(new Error('Artwork video processing failed'));
                    }
                };

                video.onerror = () => {
                    reject(new Error('Artwork video load failed'));
                }

                video.load();
            });
        } catch (error) {
            console.error("ArtworkMedia: video load failed");
            throw new Error('Artwork video load failed');
        }
    }

    /**
     * Loads and sets up image textures for artwork
     * Handles texture loading and material configuration
     * @param loader Asset loader instance
     * @param artworkModel Model to apply image to
     * @param uri Image URL
     * @param artworkWrapper Optional artwork configuration
     * @returns Promise resolving when image is loaded
     */
    static async loadNFTImage(loader: Loader, artworkModel: any, uri: string, artworkWrapper?: ArtworkWrapper): Promise<{ outputWidth: number, outputHeight: number }> {
        try {
            const _uri = getArtworkMediaUrl(uri)
            const res: any = await loader.loadTexture(_uri);
            if (!res?.image) {
                return { outputWidth: 0, outputHeight: 0 };
            }
            const width = res.image.width;
            const height = res.image.height;

            // Check if image needs resizing
            const targetTextureSize = isHighTextureQuality() ? 1024 : 512;
            let finalTexture = res;
            if (width > targetTextureSize || height > targetTextureSize) {
                // Create a canvas for resizing
                const canvas = document.createElement('canvas');
                canvas.width = targetTextureSize;
                canvas.height = targetTextureSize;
                const ctx = canvas.getContext('2d');

                if (ctx) {
                    // Draw the image resized to the active quality cap
                    ctx.drawImage(res.image, 0, 0, targetTextureSize, targetTextureSize);

                    // Create new texture from the resized canvas
                    finalTexture = new THREE.CanvasTexture(canvas);
                    finalTexture.wrapS = finalTexture.wrapT = THREE.RepeatWrapping;
                    finalTexture.repeat.set(1, 1);
                    finalTexture.colorSpace = THREE.SRGBColorSpace;
                    finalTexture.needsUpdate = true;
                }
            } else {
                finalTexture.wrapS = finalTexture.wrapT = THREE.RepeatWrapping;
                finalTexture.repeat.set(1, 1);
                finalTexture.colorSpace = THREE.SRGBColorSpace;
            }

            const imageRatio = 2 / Math.max(width, height);
            let outputWidth = isHighTextureQuality() ? 1024 : 512
            let outputHeight = isHighTextureQuality() ? 1024 : 512

            let material: any = new THREE.MeshBasicMaterial({ color: 0xffffff, map: finalTexture, transparent: true })
            if (artworkWrapper?.artworks[artworkWrapper?.activeArtwork]?.artworkData?.affectedByLights) {
                material = new THREE.MeshPhysicalMaterial({ color: 0xffffff, map: finalTexture, transparent: true, roughness: 1, metalness: 0, side: THREE.DoubleSide })
                material.userData.mapIntensity = { value: getArtworkMapIntensity() };
                material.onBeforeCompile = (shader: any) => {
                    // Inject the uniform declaration
                    shader.uniforms.mapIntensity = material.userData.mapIntensity;

                    // Inject the intensity control in the fragment shader
                    // Find the texture sampling and multiply by intensity
                    shader.fragmentShader = shader.fragmentShader.replace(
                        '#include <map_fragment>',
                        `
                        #ifdef USE_MAP
                            vec4 sampledDiffuseColor = texture2D( map, vMapUv );
                            sampledDiffuseColor.rgb *= mapIntensity;
                            diffuseColor *= sampledDiffuseColor;
                        #endif
                        `
                    );

                    // Add uniform declaration at the top of fragment shader
                    shader.fragmentShader = `uniform float mapIntensity;
        
        ` + shader.fragmentShader;
                };
            }

            if (artworkWrapper) {
                const artwork = artworkWrapper.artworks[artworkWrapper.activeArtwork]
                const resultingWidth = (artwork.artworkData.height * width) / height;
                artworkModel.scale.x = (resultingWidth * 0.01);
                outputWidth = resultingWidth * 0.01
                artworkModel.scale.y = (artwork.artworkData.height * 0.01);
                outputHeight = artwork.artworkData.height * 0.01
                artworkModel.material = material
                artworkModel.material.needsUpdate = true;
            } else {
                artworkModel.scale.x = imageRatio * width;
                outputWidth = imageRatio * width
                artworkModel.scale.y = imageRatio * height;
                outputHeight = imageRatio * height
                artworkModel.material = material
                artworkModel.material.needsUpdate = true;
            }

            return { outputWidth: outputWidth, outputHeight: outputHeight };
        } catch (error) {
            console.error("ArtworkMedia: image load failed");
            throw new Error('Artwork image load failed');
        }
    }
}
